import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { CampaignConsultantDto } from '@signalvote/shared';
import { useCampaignStore } from '@/stores';
import { useCampaignConsultant, useSetCampaignConsultant, useVolunteers, isVersionConflict } from '@/hooks/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Radix Select can't hold an empty value, so "No consultant" needs a sentinel.
const NONE = 'none';
const CONFLICT_MESSAGE = 'Someone else changed the consultant. Reloaded the latest.';

export function ConsultantCard() {
  const campaignId = useCampaignStore((s) => s.selectedCampaign?.id);
  const isOwner = useCampaignStore((s) => s.isOwner());

  if (!isOwner || !campaignId) return null;

  return <ConsultantEditor key={campaignId} campaignId={campaignId} />;
}

function ConsultantEditor({ campaignId }: { campaignId: string }) {
  const { data: consultant, isLoading } = useCampaignConsultant(campaignId);
  const { data: members = [], isLoading: membersLoading } = useVolunteers();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Consultant</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Pick the campaign member who consults for this race. The assistant coaches the campaign in chat from
          their playbook, the advice they write under Settings, Your playbook.
        </p>
        {isLoading || membersLoading || !consultant ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : (
          <ConsultantPicker
            campaignId={campaignId}
            consultant={consultant}
            options={members
              .filter((m) => m.isActive)
              .map((m) => ({ id: m.userId, name: `${m.firstName} ${m.lastName}`.trim() || m.email }))}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ConsultantPicker({
  campaignId,
  consultant,
  options,
}: {
  campaignId: string;
  consultant: CampaignConsultantDto;
  options: { id: string; name: string }[];
}) {
  const { mutateAsync: setConsultant, isPending: saving } = useSetCampaignConsultant();
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const saved = consultant.consultantUserId ?? NONE;
  const value = draft ?? saved;
  const dirty = value !== saved;
  const listed =
    consultant.consultantUserId && !options.some((o) => o.id === consultant.consultantUserId)
      ? [...options, { id: consultant.consultantUserId, name: consultant.consultantName ?? 'Former member' }]
      : options;

  const save = async () => {
    setError(null);
    try {
      await setConsultant({
        campaignId,
        request: { consultantUserId: value === NONE ? null : value, version: consultant.version },
      });
      setDraft(null);
    } catch (e) {
      if (isVersionConflict(e)) {
        setDraft(null);
        setError(CONFLICT_MESSAGE);
      } else {
        setError(e instanceof Error ? e.message : "Couldn't save the consultant. Try again.");
      }
    }
  };

  return (
    <>
      <Select
        value={value}
        onValueChange={(next) => {
          setError(null);
          setDraft(next);
        }}
        disabled={saving}
      >
        <SelectTrigger aria-label="Consultant">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>No consultant</SelectItem>
          {listed.map((o) => (
            <SelectItem key={o.id} value={o.id}>
              {o.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={save} disabled={!dirty || saving}>
          {saving ? (
            <>
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setDraft(null);
            setError(null);
          }}
          disabled={!dirty || saving}
        >
          Reset
        </Button>
      </div>
    </>
  );
}
